/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Modal,
  Divider,
  Button,
  Box,
  Select,
  Group,
  Text,
  Textarea,
  Badge,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import ModalHeader from "components/ModalHeader";
import { useAdminEditLeave } from "api/newLeave/adminEditLeave";

const LEAVE_TYPE_OPTIONS = [
  { value: "annual", label: "Annual Leave" },
  { value: "sick", label: "Sick Leave" },
  { value: "pressingNecessity", label: "Pressing Necessity" },
  { value: "compassionate", label: "Compassionate Leave" },
  { value: "bereavement", label: "Bereavement Leave" },
  { value: "juryDuty", label: "Jury Duty" },
  { value: "election", label: "Election Leave" },
  { value: "bandBusiness", label: "Band Business Leave" },
  { value: "governance", label: "Governance Leave" },
  { value: "education", label: "Education Leave" },
  { value: "professionalDevelopment", label: "Professional Development" },
  { value: "culturalSpiritual", label: "Cultural & Spiritual Leave" },
  { value: "leaveWithoutPay", label: "Leave Without Pay (LWP)" },
  { value: "otherLeave", label: "Other" },
];

type EditLeaveModalProps = {
  opened: boolean;
  onClose: () => void;
  leaveId: string;
  initialData: {
    fromDate?: string | null;
    toDate?: string | null;
    leaveType?: string | null;
    reason?: string | null;
    status?: string | null;
  };
};

export default function EditLeaveModal({
  opened,
  onClose,
  leaveId,
  initialData,
}: EditLeaveModalProps) {
  const { mutate: adminEditLeave, isLoading } = useAdminEditLeave();

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [leaveType, setLeaveType] = useState<string | null>(null);
  const [reason, setReason] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pre-populate fields when modal opens or initialData changes
  useEffect(() => {
    if (opened) {
      setFromDate(initialData.fromDate ? new Date(initialData.fromDate) : null);
      setToDate(initialData.toDate ? new Date(initialData.toDate) : null);
      setLeaveType(initialData.leaveType || null);
      setReason(initialData.reason || "");
      setErrors({});
    }
  }, [opened, initialData]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!fromDate) newErrors.fromDate = "From date is required";
    if (!toDate) newErrors.toDate = "To date is required";
    if (fromDate && toDate && fromDate > toDate) {
      newErrors.toDate = "To date must be on or after from date";
    }
    if (!leaveType) newErrors.leaveType = "Leave type is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    adminEditLeave(
      {
        leaveId,
        fromDate: fromDate!.toISOString(),
        toDate: toDate!.toISOString(),
        leaveType: leaveType!,
        reason: reason.trim() || undefined,
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  const statusColor = (s?: string | null) => {
    switch ((s || "").toLowerCase()) {
      case "approved": return "green";
      case "rejected":
      case "declined": return "red";
      case "pending": return "yellow";
      default: return "gray";
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="md"
      withCloseButton={false}
      transitionDuration={300}
      centered
      overlayOpacity={0.5}
      closeOnClickOutside={!isLoading}
      closeOnEscape={!isLoading}
    >
      <ModalHeader
        title="Edit Leave"
        onClose={handleClose}
        ariaLabel="Close edit leave modal"
      />
      <Divider mb="md" />

      {initialData.status && (
        <Group mb="sm" spacing="xs">
          <Text size="sm" color="dimmed">Current status:</Text>
          <Badge
            color={statusColor(initialData.status)}
            variant="filled"
            sx={{ textTransform: "capitalize" }}
          >
            {initialData.status}
          </Badge>
          {initialData.status === "approved" && (
            <Text size="xs" color="orange">
              Editing will recalculate used balance
            </Text>
          )}
        </Group>
      )}

      <Box style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <DatePicker
          label="From Date"
          placeholder="Select from date"
          value={fromDate}
          onChange={(val) => {
            setFromDate(val);
            if (errors.fromDate) setErrors((e) => ({ ...e, fromDate: "" }));
            // If toDate is before new fromDate, clear it
            if (val && toDate && val > toDate) setToDate(null);
          }}
          error={errors.fromDate}
          clearable
          required
        />

        <DatePicker
          label="To Date"
          placeholder="Select to date"
          value={toDate}
          onChange={(val) => {
            setToDate(val);
            if (errors.toDate) setErrors((e) => ({ ...e, toDate: "" }));
          }}
          minDate={fromDate || undefined}
          error={errors.toDate}
          clearable
          required
        />

        <Select
          label="Leave Type"
          placeholder="Select leave type"
          data={LEAVE_TYPE_OPTIONS}
          value={leaveType}
          onChange={(val) => {
            setLeaveType(val);
            if (errors.leaveType) setErrors((e) => ({ ...e, leaveType: "" }));
          }}
          error={errors.leaveType}
          searchable
          required
        />

        <Textarea
          label="Reason"
          placeholder="Enter reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={2}
          autosize
        />

        <Group position="right" mt="sm">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isLoading}>
            Save Changes
          </Button>
        </Group>
      </Box>
    </Modal>
  );
}
